import { useState, useEffect, useCallback } from 'react';
import { api } from '../context/AuthContext';

// Detect iOS Safari + standalone (PWA) mode
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return new Uint8Array(raw.split('').map((c) => c.charCodeAt(0)));
};

export const usePushNotifications = (currentUser) => {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [registering, setRegistering] = useState(false);

  const supported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  // iOS only supports Web Push for installed PWAs (16.4+)
  const iosNeedsInstall = isIOS() && !isStandalone();

  // Check current state on mount
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscribed(!!sub);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: 'unsupported' };
    if (iosNeedsInstall) return { ok: false, reason: 'ios_install_required' };

    setRegistering(true);
    try {
      // Use the existing /service-worker.js registered by index.js
      const reg = await navigator.serviceWorker.ready;

      // Ask for permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        return { ok: false, reason: 'permission_denied' };
      }

      // Get VAPID key from server
      const { data } = await api.get('/api/push/vapid-public-key');
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);

      // Subscribe with browser
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // Save to backend
      const json = sub.toJSON();
      await api.post('/api/push/subscribe', {
        endpoint: json.endpoint,
        keys: json.keys,
        expirationTime: json.expirationTime,
        user_agent: navigator.userAgent,
      });

      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      console.error('Push subscribe error:', e);
      return { ok: false, reason: 'error', error: e.message };
    } finally {
      setRegistering(false);
    }
  }, [supported, iosNeedsInstall]);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post('/api/push/unsubscribe', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (e) {
      console.error('Push unsubscribe error:', e);
    }
  }, []);

  return {
    supported,
    iosNeedsInstall,
    permission,
    subscribed,
    registering,
    subscribe,
    unsubscribe,
  };
};
